import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';
import TagEditor from 'shared/components/TagEditor';

import AdCopyPanel from './AdCopyPanel';
import CreativeNameField from './CreativeNameField';
import useStaticFormatLabels from './useStaticFormatLabels';
import { DisplayUnit, groupHasAdCopy } from './staticGroups';
import { SIMILARITY_LABEL, SIMILARITY_TIP, rowSourceAttached } from './similarityAxis';
import { GENERATION_STATUS_LABEL } from './staticStatus';
import { GenerationRow } from './types';

// The inside of a folded stack: which creatives are in it, and the one set of
// headlines and captions they all launch with.
//
// The gallery is the versions strip scaled up — same click-a-thumbnail model,
// bigger tiles, because here the tiles are whole creatives being judged rather
// than renders of one. Membership editing is deliberately per-member (remove
// one) plus one wholesale escape hatch (ungroup all); there is no "add" here,
// since adding is a selection in the grid.

interface GroupDetailDialogProps {
  open: boolean;
  unit: Extract<DisplayUnit, { kind: 'group' }> | null;
  service: StaticAdsService;
  /** Membership or copy changed server-side — reload the list. */
  /**
   * Reload the grid. Returns a promise so callers can keep their busy state up
   * until the reload FINISHES — the parent's reload is the slow half of every
   * mutation here, and a spinner that stops when the mutation returns leaves
   * seconds of dead air where the dialog looks idle but the data is stale.
   */
  onChanged: () => void | Promise<void>;
  onClose: () => void;
  /** Open one member's own detail dialog, layered above this one. */
  onOpenMember: (row: GenerationRow) => void;
  /** Tag autocomplete options — the union across ads, creatives and groups. */
  tagSuggestions?: string[];
  /**
   * A tag was ADDED to the stack — lets the parent refresh its tag
   * vocabularies. Removal deliberately does not fire it, matching the
   * generation-level tag editor.
   */
  onTagAdded?: () => void;
}

/** What the user is about to do, held until they confirm it. */
type PendingAction =
  | { kind: 'ungroup' }
  | { kind: 'remove'; generationId: string };

export default function GroupDetailDialog({
  open, unit, service, onChanged, onClose, onOpenMember, tagSuggestions = [], onTagAdded,
}: GroupDetailDialogProps) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [ungrouping, setUngrouping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const templateLabel = useStaticFormatLabels(service);

  const members = unit?.rows || [];
  const info = unit?.info;

  // Tags are held locally so the editor updates the instant the server answers
  // — the parent's reload is what eventually agrees. Re-seeded per stack, not
  // per render, so opening another group can't leave the previous one's tags.
  const groupId = unit?.groupId || null;
  const [tags, setTags] = useState<string[]>(info?.tags || []);
  useEffect(() => { setTags(info?.tags || []); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const leader = members[0];
  // Both of these destroy the group row, and the shared copy lives ON it.
  // Counted server-side, not from the visible rows: a filter can hide members.
  const removeDissolvesGroup = !!info && info.memberCount <= 2;
  const losesSharedCopy = !!info && groupHasAdCopy(info);

  const removeMember = async (generationId: string) => {
    if (!unit) return;
    setPending(null);
    setBusyId(generationId);
    setError(null);
    try {
      const resp = await service.removeGroupMember(unit.groupId, generationId);
      await onChanged();
      // Dropping below two members dissolves the group server-side — there is
      // no stack left to look at, so the dialog goes with it.
      if (resp.dissolved) onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Removing that creative failed');
    } finally {
      setBusyId(null);
    }
  };

  const ungroupAll = async () => {
    if (!unit) return;
    setPending(null);
    setUngrouping(true);
    setError(null);
    try {
      await service.deleteGenerationGroup(unit.groupId);
      await onChanged();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Ungrouping failed');
    } finally {
      setUngrouping(false);
    }
  };

  const busy = !!busyId || ungrouping;

  return (
    <Dialog open={open && !!unit} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      {unit && info && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            {info.name || `Group of ${info.memberCount} — ${leader?.productName || 'no product'}`}
          </DialogTitle>
          {/* A bar across the whole dialog, not just the 18px spinner on the
              tile being removed. Every one of these actions reloads the grid
              afterwards, which is seconds of Snowflake round trips — long
              enough that a corner spinner reads as "nothing happened". */}
          {busy && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ px: 3, py: 0.5, display: 'block' }}>
                {ungrouping ? 'Ungrouping…' : 'Removing from group…'}
              </Typography>
            </Box>
          )}
          <DialogContent dividers>
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Name and tags identify the GROUP, so they sit above its contents. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CreativeNameField
                  value={info.name}
                  label="Group name"
                  placeholder="Name this group…"
                  disabled={busy}
                  onSave={async (name) => {
                    await service.saveGroupName(info.groupId, name);
                    onChanged();
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TagEditor
                  tags={tags}
                  suggestions={tagSuggestions}
                  disabled={busy}
                  label="Group tags"
                  onAdd={async (tag) => {
                    setTags((await service.addGroupTag(info.groupId, tag)).tags);
                    onChanged();
                    onTagAdded?.();
                  }}
                  onRemove={async (tag) => {
                    setTags((await service.removeGroupTag(info.groupId, tag)).tags);
                    onChanged();
                  }}
                />
              </Box>
            </Stack>

            <Typography variant="overline" color="text.secondary">
              Creatives in this group
            </Typography>
            {/* Tiles are sized for JUDGING the creative, not for indexing it — these
                are finished ads whose copy and composition have to be legible before
                anyone decides what ships, so the group shows a handful of big ones
                rather than a contact sheet of thumbnails. */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5, mt: 0.5,
            }}>
              {members.map(row => (
                <Box
                  key={row.generationId}
                  sx={{
                    position: 'relative', aspectRatio: '1 / 1', bgcolor: 'grey.100', borderRadius: 1,
                    overflow: 'hidden', border: '1px solid', borderColor: 'divider',
                    // Keyboard users get the action the mouse reveals.
                    '&:hover .member-remove, &:focus-within .member-remove': { opacity: 1 },
                  }}
                >
                  {/* The tile itself opens the creative — a dedicated Open button on
                      every tile competed with the remove action for the same space and
                      made the obvious gesture (click the picture) look unsupported. */}
                  <CardActionArea
                    onClick={() => onOpenMember(row)}
                    disabled={busyId === row.generationId}
                    sx={{ width: '100%', height: '100%' }}
                  >
                    {row.outputImageUrl ? (
                      <Box component="img" src={row.outputImageUrl} alt="Grouped creative" loading="lazy"
                        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                    ) : (
                      <Stack alignItems="center" justifyContent="center" spacing={1}
                        sx={{ height: '100%', color: 'text.disabled' }}>
                        {(row.status === 'PENDING' || row.status === 'RUNNING') && <CircularProgress size={20} />}
                        <Typography variant="caption">{GENERATION_STATUS_LABEL[row.status] || row.status}</Typography>
                      </Stack>
                    )}
                  </CardActionArea>

                  {row.similarity && (
                    <Tooltip title={SIMILARITY_TIP[row.similarity] || ''}>
                      <Chip
                        size="small"
                        variant="filled"
                        color={rowSourceAttached(row) ? 'warning' : 'default'}
                        label={SIMILARITY_LABEL[row.similarity] || row.similarity}
                        sx={{
                          position: 'absolute', bottom: 4, left: 4, height: 20, fontSize: '0.65rem',
                          maxWidth: 'calc(100% - 8px)',
                        }}
                      />
                    </Tooltip>
                  )}

                  {/* Removal is the rare, destructive gesture, so it gets a small
                      corner target that stays out of the creative's way — not half
                      the tile. It reveals on hover/focus so it cannot be hit while
                      scanning the group. */}
                  {busyId === row.generationId ? (
                    <CircularProgress
                      size={18}
                      sx={{ position: 'absolute', top: 6, right: 6, color: 'common.white' }}
                    />
                  ) : (
                    <Tooltip title="Remove from group">
                      <Box
                        component="span"
                        className="member-remove"
                        sx={{
                          position: 'absolute', top: 3, right: 3, opacity: 0, transition: 'opacity 0.15s',
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label="Remove from group"
                          disabled={busy}
                          onClick={() => (removeDissolvesGroup
                            ? setPending({ kind: 'remove', generationId: row.generationId })
                            : removeMember(row.generationId))}
                          sx={{
                            p: 0.25, color: 'common.white', bgcolor: 'rgba(0,0,0,0.55)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>

            {members.length < info.memberCount && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {members.length} of {info.memberCount} shown — the rest are hidden by the current filter.
              </Typography>
            )}

            <Box sx={{ mt: 3 }}>
              {/* One panel, one save, every member. The first member is only the
                  studio's host (it supplies the source ad and product context);
                  what gets written goes to the GROUP. */}
              <Typography variant="caption" color="text.secondary">
                Shared across {info.memberCount} grouped creatives
              </Typography>
              {leader && (
                <AdCopyPanel
                  row={leader}
                  group={info}
                  service={service}
                  onSaved={onChanged}
                  formatLabel={templateLabel(leader.formatKey) || null}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Tooltip title="Break the group apart — the creatives stay, they just stop sharing copy and publish separately">
              <span>
                <Button color="warning" onClick={() => setPending({ kind: 'ungroup' })} disabled={busy}>
                  {ungrouping ? 'Ungrouping…' : 'Ungroup all'}
                </Button>
              </span>
            </Tooltip>
            <Button onClick={onClose} disabled={busy}>Close</Button>
          </DialogActions>

          <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
            <DialogTitle>
              {pending?.kind === 'ungroup' ? 'Break this group apart?' : 'Remove this from the group?'}
            </DialogTitle>
            <DialogContent>
              {/* The remove case says nothing beyond the title. The mechanics of
                  what a one-member group does are ours to handle, not something
                  to make the operator read before every click — the only thing
                  worth confirming is the intent, and the shared-copy warning
                  below is the one real consequence. */}
              {pending?.kind === 'ungroup' && (
                <Typography variant="body2" color="text.secondary">
                  {`The ${info.memberCount} creatives stay — they just stop sharing a card and publish separately.`}
                </Typography>
              )}
              {/* Only a group that HAS copy loses any — saying so unconditionally
                  would train the operator to click through a real warning. */}
              {losesSharedCopy && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  The headlines and captions saved on this group are <strong>deleted with it</strong>,
                  permanently. Copy saved on the individual creatives is not affected.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPending(null)}>Cancel</Button>
              <Button
                color="error" variant="contained"
                onClick={() => {
                  if (!pending) return;
                  if (pending.kind === 'ungroup') void ungroupAll();
                  else void removeMember(pending.generationId);
                }}
              >
                {pending?.kind === 'ungroup' ? 'Ungroup all' : 'Remove'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Dialog>
  );
}
